import React from 'react';
import { countBlocking, blockingHeading } from '../utils/specCheck';

/* ★ THE STEP-5 EMPLOYEE-FLAG RENDERER — the reader `parseEmployeeExcel`'s flags never had.
 *
 * `399d79d` (the missing-Salary-column notice) and `b4d78a8` (the `severity` / `scope`
 * vocabulary) both SHIPPED INERT: `step5Parsed.flags` had ZERO readers, so eleven flag
 * literals were computed on every employee upload and thrown away. This component is what
 * makes them visible. Employee was the ONLY import parser whose flags reached no screen —
 * financial (Step 2b), vendor + prepaid (Step 6b) and asset (Step 8) all render theirs.
 *
 * ⛔ THIS IS DELIBERATELY A FOURTH IMPLEMENTATION, NOT AN EXTRACTION — Brian's call, taken
 *    on the measurement in `diffs/notice-renderer-scope.txt`: of 9 behaviours across the
 *    three existing inline renderers, **2 are shared by all three, 3 by two (and the one
 *    they nominally share — severity → icon — uses a DIFFERENT predicate in each), and 6
 *    are unique to exactly one screen.** A component covering the union would carry six
 *    modes to reproduce today's output, put three working screens at risk, and Step 2b
 *    could not be made output-identical at all without moving sentence composition into
 *    parseFinancialExcel — a PARSER change. The extraction was scoped and DECLINED.
 *    ⓘ Principle 0 is not silent here and this is not a free pass: the duplication is
 *      REAL and is accepted with its reason stated, which is §6's escape hatch (3) —
 *      "make any unavoidable divergence EXPLICIT, MINIMAL, and named in the docs".
 *      What would retire it: the day a fourth screen wants the same shape, extract the
 *      GENERIC one (Step 6b's — already reused once, by AdminPage's own 4 authored vendor
 *      flags) and leave 2b's composed-sentence renderer where it is.
 *
 * ⛔ RENDERS ONLY. No parsing, no state, no fetch, no engine call, no number. Props in,
 *    JSX out — so it cannot move a stored value and no golden can see it.
 *
 * ⛔ SEVERITY COMES FROM `f.severity`, NEVER FROM `f.type`. That conflation is exactly
 *    what `b4d78a8` removed from this parser, and re-reading `type` as a level would
 *    reintroduce it. ⚠ It also matters in the other direction: Step 2b's and Step 6b's
 *    renderers test `f.severity === 'warning' || f.type === 'formula'` — pasting either
 *    one here would still work, but only because this parser NOW sets `severity`. Before
 *    `b4d78a8` it set `type: 'warning'` and no `severity`, so a verbatim paste would have
 *    rendered all eight warnings as ℹ️ INFO — a silent severity DOWNGRADE.
 */

const FONT = "'DM Sans',-apple-system,sans-serif";

/* SEVERITY → ICON. The parser emits exactly three levels (measured over its 11 literals:
 * error×2, warning×8, info×1). An unrecognised level falls back to ℹ️ rather than throwing
 * — a renderer must not be able to blank a screen over a vocabulary it does not know. */
const SEVERITY_ICON = {
    blocking: '\u26D4',      // W-068: the upload check's blocking level (the same no-entry sign)
    error:   '⛔',            // no-entry
    warning: '⚠️',      // the Step-2b / Step-6b spelling, character for character
    info:    'ℹ️',
};
const iconFor = (sev) => SEVERITY_ICON[sev] || SEVERITY_ICON.info;

/* ★★ THE COLLAPSE, AND WHERE ITS CAUSAL LINK LIVES.
 *
 * A sheet- or column-level fact can CAUSE a row-level flag on every row. Left flat, the
 * cause is a minority of its own symptoms: measured on the forged worst case (the real
 * 62-row roster with its Salary column removed) the array is **64 flags, of which the one
 * that explains the other 62 is 1 — 1.6% of the list**, in a list sorted by nothing.
 * Step 6 already judged 26 identical sentences "a wall" and collapses them; 62 is worse.
 *
 * ⛔ THE LINK IS ESTABLISHED FROM `type`, NEVER FROM MESSAGE TEXT. Matching a sentence is
 *    a name-guess and §E1 forbids it as a classification mechanism. `type` is the honest
 *    separator the objects actually carry.
 *
 * ★ WHERE THE MAPPING LIVES — SAID PLAINLY BECAUSE IT IS A DIVERGENCE FROM THE PRECEDENT:
 *   it lives HERE, in the renderer. Step 6b's collapse groups on an explicit `f.cause`
 *   marker that its PARSER writes; parseEmployeeExcel carries no equivalent field and this
 *   build is forbidden from touching it. ⇒ the honest home for this map is the parser (a
 *   `cause` field at the two push sites), and moving it there is a later build. Until then
 *   the map is a declared, minimal divergence — not a guess: the pairing below is a fact
 *   about the parser's control flow, readable at the push sites.
 *
 * ★ IT HAS EXACTLY ONE ENTRY, AND THAT IS MEASURED, NOT AN OVERSIGHT. Of the parser's five
 *   non-row flags only `no_salary_column` has row-level consequences:
 *     · `ignored_columns`   — reports unrecognised HEADERS; raises no row flag.
 *     · `misspelled_header` — the header still MATCHES (`matchCoreColumn` resolves it and
 *                             the misspelling is reported after), so Commission Rate IS
 *                             read and `missing_commission_rate` does not follow from it.
 *     · `no_sheet` / `empty_sheet` — both return with `employees: []`, so there are no
 *                             rows to raise a row flag at all.
 */
const CAUSE_TO_ROW_TYPES = {
    // A sheet with no Salary column makes EVERY row's salary read as absent, because the
    // per-row read maps column-absent and cell-empty onto the same 0. One cause, N rows.
    no_salary_column: ['missing_salary'],
};

/* The summary sentence a collapsed run is replaced by. It must STATE THE COUNT — the count
 * is the information, which is why this is a line and not a disclosure control the operator
 * has to click to discover a number. */
const COLLAPSE_SUMMARY = {
    missing_salary: (n) => `${n} employee${n !== 1 ? 's' : ''} in this file have no salary.`,
};

const ROW_SCOPES = new Set(['row']);

export default function EmployeeFlagNotices({ flags }) {
    const all = Array.isArray(flags) ? flags : [];
    /* ⛔ NOTHING AT ALL WHEN EMPTY — not an empty-state panel. Step 5's own screens already
     * report success by counting ("We detected N benefit columns", "Review N employees"),
     * so a green "no issues" panel here would be a second, competing summary. Step 6b has
     * one because it IS a review sub-step with nothing else on it; Step 5 is not. */
    if (all.length === 0) return null;

    /* Which causes are actually PRESENT — a collapse may only fire when the flag that
     * explains the run is on screen to be collapsed under. */
    const presentCauses = all.filter(f => CAUSE_TO_ROW_TYPES[f.type]);

    /* rowType -> the cause flag that explains it, for the causes present in THIS array. */
    const causeOfRowType = {};
    for (const c of presentCauses) {
        for (const t of CAUSE_TO_ROW_TYPES[c.type]) causeOfRowType[t] = c.type;
    }

    /* Count the caused row flags per row-type. ⛔ Only `scope: 'row'` flags are ever folded
     * — a sheet- or column-level fact is never a consequence of another one. */
    const causedCounts = {};
    for (const f of all) {
        if (!ROW_SCOPES.has(f.scope)) continue;
        if (!causeOfRowType[f.type]) continue;
        causedCounts[f.type] = (causedCounts[f.type] || 0) + 1;
    }

    /* ★ NO `> 1` THRESHOLD, AND THE DIVERGENCE FROM STEP 6b IS DELIBERATE. There the
     * collapsed rows are PEERS — N GL lines each independently missing a category — and a
     * single one is worth naming. Here they are CONSEQUENCES of a cause that is already on
     * screen saying so; "Ada Lovelace has no annual salary" directly beneath "this sheet
     * has no Salary column" restates it rather than adding to it, at any count. */
    const collapsedTypes = new Set(Object.keys(causedCounts));

    /* ⛔ THE MUST-NOT-COLLAPSE ARM: with no cause flag present, `causeOfRowType` is empty,
     * `causedCounts` is empty, and every row flag lists individually NAMING ITS EMPLOYEE.
     * Three people who genuinely have no salary on a sheet that HAS the column are three
     * separate, individually actionable facts. A collapse that always fires hides data. */
    const shown = all.filter(f => !(ROW_SCOPES.has(f.scope) && collapsedTypes.has(f.type)));

    return (
        <div style={{ marginBottom: 16 }}>
            {/* Counts the flags that EXIST, not the lines drawn — the same convention as
                Step 6b, whose heading counts `flags.length` while its collapse removes
                rows from the list beneath it. The summary line reconciles the two. */}
            <div style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, color: '#1A1C2E', marginBottom: 8 }}>
                We found {all.length} item{all.length !== 1 ? 's' : ''} to review in your file
            </div>
            {/* W-068: the upload check's ONE heading (the same sentence as Steps 2b, 6b and 8) when the file has blocking
                problems; the step's Continue is disabled until the file is fixed and re-uploaded. */}
            {countBlocking(all) > 0 && (
                <div data-spec-heading="blocking" style={{ padding: '10px 12px', marginBottom: 8, background: '#FEF3F2', border: '1px solid #FDA29B',
                    borderRadius: 6, fontSize: 13, fontFamily: FONT, color: '#B42318', fontWeight: 600 }}>
                    {'\u26D4'} {blockingHeading(countBlocking(all))}
                </div>
            )}
            {/* Scroll, do not truncate — the Step 2b / Step 6b answer to volume. Step 8's
                cap at 8 is that screen's choice, not a house rule, and a cap would hide a
                count the operator needs. After collapsing, the measured worst case is 3
                lines; the box exists for an unmeasured roster with many distinct faults. */}
            <div style={{ maxHeight: 400, overflowY: 'auto', border: '1px solid #E0E0E0', borderRadius: 6 }}>
                {shown.map((f, i) => {
                    const causedType = (CAUSE_TO_ROW_TYPES[f.type] || []).find(t => collapsedTypes.has(t));
                    const n = causedType ? causedCounts[causedType] : 0;
                    const summarize = causedType && COLLAPSE_SUMMARY[causedType];
                    return (
                        <React.Fragment key={i}>
                            <div style={{
                                padding: '8px 12px',
                                borderBottom: (summarize || i < shown.length - 1) ? '1px solid #F0F0F0' : 'none',
                                fontSize: 13, fontFamily: FONT, display: 'flex', gap: 8, alignItems: 'flex-start',
                            }}>
                                <span>{iconFor(f.severity)}</span>
                                <span>{f.message}</span>
                            </div>
                            {summarize && (
                                /* The consequence, immediately beneath its cause and visually
                                   subordinate to it — indented, muted, no icon of its own. */
                                <div style={{
                                    padding: '6px 12px 8px 40px',
                                    borderBottom: i < shown.length - 1 ? '1px solid #F0F0F0' : 'none',
                                    fontSize: 12, fontFamily: FONT, color: '#7A5B00', background: '#FFFDF5',
                                }}>
                                    {summarize(n)}
                                </div>
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
}
